import { useId, type ReactNode, type JSX } from 'react';

import { SkeletonBlock, SkeletonGroup, Switch } from '@bookora/ui';

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  THE SMALL PIECES THE HAND-BUILT FORMS KEPT RE-DRAWING
 * ════════════════════════════════════════════════════════════════════════════
 *
 * The settings, staff and CRM screens are forms that do not use `Form.Item`,
 * and each of them had grown its own copy of three things: a switch with a
 * sentence beside it that was not its label, an «تغییرات ذخیره‌نشده» line,
 * and a generic paragraph skeleton standing in for a screen of fields. One
 * definition each, here.
 */

/* -------------------------------------------------------------------------- */
/* A switch with its sentence                                                 */
/* -------------------------------------------------------------------------- */

export interface SwitchFieldProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** The sentence the switch turns on or off. It is the accessible name. */
  label: ReactNode;
  /** A consequence, under the sentence. */
  hint?: ReactNode;
  disabled?: boolean;
  loading?: boolean;
  size?: 'small' | 'default';
}

/**
 * `<Space><Switch /><Text>…</Text></Space>` gave the switch no name at all — a
 * screen reader announced «switch, off» with nothing about what it switched —
 * and made the 28px track the only thing that could be clicked. Wrapping both
 * in one `<label>` names the switch by its sentence and turns the whole line
 * into the hit area.
 */
export function SwitchField({
  checked,
  onChange,
  label,
  hint,
  disabled = false,
  loading = false,
  size,
}: SwitchFieldProps): JSX.Element {
  // Named by the sentence alone and described by the hint: the implicit
  // `<label>` name would otherwise read the consequence as part of the name.
  const labelId = useId();
  const hintId = useId();
  return (
    <label className="bk-switch-field" data-disabled={disabled ? 'true' : undefined}>
      <Switch
        checked={checked}
        disabled={disabled}
        loading={loading}
        size={size}
        onChange={onChange}
        aria-labelledby={labelId}
        aria-describedby={hint ? hintId : undefined}
      />
      <span className="bk-switch-field__text">
        <span id={labelId} className="bk-switch-field__label">
          {label}
        </span>
        {hint ? (
          <span id={hintId} className="bk-switch-field__hint">
            {hint}
          </span>
        ) : null}
      </span>
    </label>
  );
}

/* -------------------------------------------------------------------------- */
/* Unsaved changes                                                            */
/* -------------------------------------------------------------------------- */

export interface DirtyNoteProps {
  dirty: boolean;
  /** Shown while there is something to save. */
  dirtyText?: string;
  /** Shown while there is not. `null` renders nothing in that state. */
  cleanText?: string | null;
}

/**
 * The line beside a Save button that says whether pressing it would change
 * anything. `role="status"` so the first edit is announced once, politely,
 * without stealing focus from the field being typed in.
 */
export function DirtyNote({
  dirty,
  dirtyText = 'تغییرات ذخیره‌نشده دارید.',
  cleanText = 'همهٔ تغییرات ذخیره شده‌اند.',
}: DirtyNoteProps): JSX.Element {
  const text = dirty ? dirtyText : cleanText;
  return (
    <span className="bk-dirty-note" data-dirty={dirty ? 'true' : 'false'} role="status">
      <span className="bk-dirty-note__dot" aria-hidden="true" />
      {text ? (
        // Keyed on the state, so the swap is a fresh crossfade rather than a
        // text node changing under a finished animation.
        <span key={dirty ? 'dirty' : 'clean'} className="bk-dirty-note__text">
          {text}
        </span>
      ) : null}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Loading shapes                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Cards of labelled fields, the shape of a settings form while it loads — so
 * the page does not reflow from three grey paragraphs into four titled cards
 * when the data lands.
 */
export function FormSkeleton({
  cards = 2,
  fields = 4,
  label = 'در حال بارگذاری فرم…',
}: {
  cards?: number;
  fields?: number;
  label?: string;
}): JSX.Element {
  return (
    <SkeletonGroup label={label} className="bk-form-skeleton">
      {Array.from({ length: cards }, (_, card) => (
        <div key={card} className="bk-form-skeleton__card">
          <SkeletonBlock shape="text" width="28%" height={14} />
          <div className="bk-form-skeleton__grid">
            {Array.from({ length: fields }, (__, field) => (
              <div key={field} className="bk-form-skeleton__field">
                <SkeletonBlock shape="text" width="40%" />
                <SkeletonBlock height={32} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </SkeletonGroup>
  );
}

/** Rows of a table while it loads: a wide first column, then short cells. */
export function TableSkeleton({
  rows = 5,
  label = 'در حال بارگذاری فهرست…',
}: {
  rows?: number;
  label?: string;
}): JSX.Element {
  return (
    <SkeletonGroup label={label} className="bk-table-skeleton">
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className="bk-table-skeleton__row">
          <SkeletonBlock shape="text" width={row % 2 === 0 ? '70%' : '55%'} />
          <SkeletonBlock shape="text" width="60%" />
          <SkeletonBlock shape="text" width="50%" />
          <SkeletonBlock shape="text" width="80%" />
        </div>
      ))}
    </SkeletonGroup>
  );
}
